import classNames from 'clsx';
import React, { memo, useEffect, useState } from 'react';
import TruncateMiddle from 'react-truncate-string';
import { Hint } from 'reactApp/ui/Hint/Hint';

import styles from './Name.css';

interface IProps {
    name: string;
    extension?: string;
    fullWidth?: boolean;
    showTooltip?: boolean;
    light?: boolean;
    truncate?: boolean;
    delayTruncate?: boolean;
    showUnknownExtension?: boolean;
    disableEllipsis?: boolean;
}

export const NameComponent = memo(
    ({
        name,
        extension = '',
        fullWidth = false,
        light = false,
        showTooltip = false,
        truncate = false,
        delayTruncate = false,
        showUnknownExtension = false,
        disableEllipsis = false,
    }: IProps) => {
        let showExt = !!extension;

        if (extension === 'unknown' && !showUnknownExtension) {
            showExt = false;
        }

        const [truncateVal, setTruncateVal] = useState(delayTruncate ? false : truncate);

        useEffect(() => {
            if (!delayTruncate) {
                setTruncateVal(truncate);
                return;
            }

            if (!truncate) {
                return;
            }

            // Иногда TruncateMiddle не определяет ширину компонента и делает слишком большой truncate
            // Но при ресайзе - все становится ок.
            // В таких случаях передаем delayTruncate и сначала рендерим полную строку, потом уже делаем ей truncate
            // Пока это проявляется только на одиночных пабликах.
            // Возможно, из-за рендеринга компонента в портале в старом коде, в componentDidMount бывает еще нет ref на ноду
            const timerId = window.setTimeout(() => setTruncateVal(true));
            return () => {
                window.clearTimeout(timerId);
            };
        }, [delayTruncate, truncate]);

        const Wrap = (
            <div className={styles.wrap}>
                <div
                    className={classNames({
                        [styles.name]: true,
                        [styles.multiline]: disableEllipsis,
                    })}
                >
                    {truncateVal ? <TruncateMiddle text={name} /> : name}
                </div>
                {showExt && <div className={styles.extension}>.{extension}</div>}
            </div>
        );

        return (
            <div
                className={classNames({
                    [styles.root]: true,
                    [styles.root_fullWidth]: fullWidth,
                    [styles.root_light]: light,
                })}
            >
                {showTooltip ? <Hint text={`${name}${showExt ? `.${extension}` : ''}`}>{Wrap}</Hint> : Wrap}
            </div>
        );
    }
);

NameComponent.displayName = 'Name';
